import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import {
  EconomicDeclarationJuridicalShape,
  EconomicDeclarationShape,
  MonthlyAmountSchema
} from '../entities/client/economic'
import {
  EquityAssetsSchema,
  EquityBankSchema,
  EquityDeclarationJuridicalShape,
  EquityDeclarationShape
} from '../entities/client/equity'
import { IndividualApplicant, IndividualApplicantShape } from '../entities/client/individual'
import { LegalRepresentativeSchema } from '../entities/client/legal-representatives'
import { PepDeclarationShape } from '../entities/client/pep'
import { CompanyApplicant, CompanyApplicantShape } from '../portal/juridical/company'
import { requiredString } from '../primitives/string'
import { InstantSchema, LocalDateSchema } from '../primitives/time'
import { openShape } from '../utils/open-shape'
import { storedShape } from '../utils/stored-shape'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de LECTURA del expediente de un cliente (FEIC interno).
 *
 * Reemplaza a `GET /clients/:id`, cuya respuesta era `{ client, ...camposDeLaPersona }`
 * —los campos de la persona aplanados al lado del objeto `client`, sin envoltura—.
 * Acá el expediente tiene una forma: la ficha del cliente arriba y UNA de las dos
 * personas colgando, según `typePerson`.
 *
 * DE DÓNDE SALEN LOS TIPOS. En Mongo, `person-individual`/`person-juridical`
 * guardan `applicant`, `pep`, `economicInformation` y `equityStatement` como
 * `Mixed`: no hay contrato en la base. El contrato existe igual, en el SSOT de
 * `entities/client` y `portal/juridical` que ya validan esos mismos datos al
 * escribirlos. Este archivo los consume en su forma base (sin las reglas cruzadas
 * de submit) porque una lectura devuelve lo que hay guardado, incluidos los
 * expedientes parciales y los viejos, anteriores a que la regla existiera.
 *
 * CINCO CAMPOS DEL LEGACY NO SE PUBLICAN: `fatca`, `kycForm`, `products`,
 * `signatories` y `beneficiaries`. Están declarados en los schemas de Mongo y
 * **no tienen un solo dato**: cero en los 401 expedientes individuales y cero en
 * los 35 jurídicos. Son fósiles del esquema. Copiarlos al contrato v2 sería
 * heredar deuda y obligar a los consumidores a manejar campos que nunca llegan.
 */

/** Autoría/asesor wire-safe: `usr_`, nunca el `_id` ni el documento del usuario. */
export const V2AdminClientUserRefSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string(),
    /** Foto de perfil. La tabla la pinta como avatar; `null` cae a la inicial. */
    avatar: z.string().nullable()
  })
  .nullable()

/**
 * Vigencia del contrato del expediente.
 *
 * En el legacy estos campos se llaman `startAt`/`updateAt`/`endAt` y guardan un
 * `YYYY-MM-DD` como string suelto: fechas de calendario disfrazadas de
 * instantes. En v2 se llaman por lo que son. El sufijo no es cosmético — es el
 * contrato: `*At` es un instante y `*Date` es un día, y el interceptor de wire
 * lo hace cumplir.
 *
 * Nulos cuando no hay dato. El legacy usaba el string vacío, que obliga a cada
 * consumidor a saber que `''` significa «sin fecha».
 */
export const V2AdminClientContractSchema = z.object({
  startDate: LocalDateSchema.nullable(),
  updateDate: LocalDateSchema.nullable(),
  endDate: LocalDateSchema.nullable()
})

/** Lugar administrativo del expediente (país + códigos de depto/municipio). */
export const V2AdminClientPlaceSchema = z.object({
  country: z.string(),
  department: z.number().int().nullable(),
  municipality: z.number().int().nullable()
})

/**
 * Constancia de verificación contra RENAP. El portal bloquea como sólo-lectura
 * exactamente los campos que RENAP llenó, así que `fields` es parte del contrato
 * y no un detalle interno.
 */
export const V2AdminClientRenapVerificationSchema = z
  .object({
    cui: z.string(),
    verifiedAt: InstantSchema,
    fields: z.array(z.string())
  })
  .nullable()

/**
 * Instantánea de vigencia que sincroniza SAP. Es la que dirige el reloj anual de
 * revalidación; se publica para que la UI pueda explicar POR QUÉ un expediente
 * vence, en vez de mostrar una fecha sin origen.
 */
export const V2AdminClientRevalidationSchema = z.object({
  lastKycVerifiedAt: InstantSchema.nullable(),
  sapStatus: z.enum(['vigente', 'vencido', 'desconocido']).nullable(),
  /**
   * Es una FECHA de calendario, no un instante: la promesa vence un día, no a
   * una hora. Mongo la guarda como `Date` a medianoche, que es la forma en que
   * un motor sin tipo fecha representa un día — pero eso no la convierte en un
   * momento del tiempo.
   */
  promiseDate: LocalDateSchema.nullable(),
  contractClosedAt: InstantSchema.nullable(),
  sourceNegotiationCode: z.number().int().nullable(),
  sourceOrderSap: z.number().int().nullable(),
  checkedAt: InstantSchema.nullable()
})

/**
 * Una sección del expediente que ENTRA — contrato de ESCRITURA.
 *
 * Abierta, porque el formulario interno nombra varias cosas distinto del SSOT
 * —`dependecyRelations` con su typo, `businessRelationship`, `entityAddress`— y
 * marca cada fila repetible con `disabled`. Declararla cerrada haría que la
 * escritura BORRE todo eso sin decir nada. Ver `openShape`.
 *
 * Lo declarado SE VALIDA: una cuenta bancaria sin número no es una cuenta, y el
 * `PATCH` tiene que rechazarla. Ésa es la diferencia con `storedSection`.
 */
const writeSection = <T extends z.ZodRawShape>(shape: z.ZodObject<T>) => openShape(shape.partial())

/**
 * La misma forma, del lado de la RESPUESTA: es la que arma el OpenAPI y el SDK.
 *
 * Publicar y EXIGIR dejaron de ser lo mismo — ver `V2AdminClientStoredSchema`.
 */
const publishedSection = writeSection

/** Cómo se declara una sección: la forma que se PUBLICA o la que se EXIGE. */
type SectionMode = typeof publishedSection

/**
 * La sección tal como el REGISTRO LA GUARDÓ — la que se EXIGE al validar una respuesta.
 *
 * LA ASIMETRÍA ES EL PUNTO, y confundirla costó tres expedientes rotos en
 * producción. Al escribir, el contrato representa a quien recibe el dato y tiene
 * derecho a exigir: sin número no hay cuenta. Al leer representa a quien ya lo
 * guardó, y no puede exigir nada que no se le haya exigido en su momento — el
 * expediente ya existe, con sus campos vacíos y su historia.
 *
 * Durante un tiempo las dos fueron el MISMO helper, y por eso la lectura heredó
 * las reglas del submit: `requiredString()`, `requiredMoney()`, `.min(1)`. Un
 * expediente a medio llenar tumbaba la pantalla entera. `storedShape` explica
 * qué afloja y qué sigue rechazando.
 *
 * El `.partial()` se conserva por el TIPO: `storedShape` afloja el schema en
 * runtime pero declara devolver el mismo tipo, así que sin él los consumidores
 * verían estas secciones como obligatorias. Con él, el tipo publicado no cambia.
 */
const storedSection = <T extends z.ZodRawShape>(shape: z.ZodObject<T>) =>
  storedShape(shape.partial())

/**
 * La moneda de una fila, TAL COMO v2 LA EMITE.
 *
 * `stripRowKeys` traduce a ISO la etiqueta que reconoce y manda el resto como
 * `currencyLabel` —que es lo que ese valor siempre fue, una etiqueta— en vez de
 * negarse a devolver lo que el cliente declaró. Producción tiene 459 «QUETZAL»,
 * 46 «DOLAR» y un «DIRHAM», todos tecleados en el `<input>` libre del FEIC
 * interno, contra 546 «GTQ» que vienen del selector del portal.
 *
 * `storedShape` ya evita que la ausencia de `currency` tumbe el expediente, y
 * `passthrough()` ya deja pasar `currencyLabel`. Esto se declara igual porque un
 * contrato está para DECIR lo que la API emite: el campo aparece en el OpenAPI y
 * en el SDK, y quien consuma el expediente se entera de que existe sin tener que
 * leer el servicio. Es documentación con tipos, no validación.
 */
const wireCurrency = { currencyLabel: z.string().optional() }

/** Los activos son un solo schema compartido por individual y jurídica (`equityCore`). */
const wireAssets = {
  assets: EquityAssetsSchema.extend({
    banks: z.array(EquityBankSchema.extend(wireCurrency)).default([])
  }).default({})
}

/** Ídem para los montos mensuales: `JuridicalMonthlyAmountSchema` es un alias del mismo. */
const wireMonthlyAmounts = {
  monthlyIncomes: z.array(MonthlyAmountSchema.extend(wireCurrency)).default([]),
  monthlyExpenses: z.array(MonthlyAmountSchema.extend(wireCurrency)).default([])
}

/** Expediente de una persona individual. */
const individualSchema = (section: SectionMode) =>
  z.object({
    applicant: section(IndividualApplicantShape),
    pep: section(PepDeclarationShape).nullable(),
    economicInformation: section(EconomicDeclarationShape.extend(wireMonthlyAmounts)).nullable(),
    equityStatement: section(EquityDeclarationShape.extend(wireAssets)).nullable(),
    /**
     * Casi siempre vacío en individuales (1 de 401): un individuo se representa a
     * sí mismo. Se publica porque el caso existe —un menor o un interdicto— y
     * omitirlo obligaría a leer el legacy para ese puñado de expedientes.
     */
    legalRepresentatives: z.array(section(LegalRepresentativeSchema)),
    photo: z.string().nullable(),
    renapVerification: V2AdminClientRenapVerificationSchema,
    revalidation: V2AdminClientRevalidationSchema
  })

/** Expediente de una persona jurídica. */
const juridicalSchema = (section: SectionMode) =>
  z.object({
    applicant: section(CompanyApplicantShape),
    economicInformation: section(
      EconomicDeclarationJuridicalShape.extend(wireMonthlyAmounts)
    ).nullable(),
    equityStatement: section(EquityDeclarationJuridicalShape.extend(wireAssets)).nullable(),
    /** Acá sí es la regla y no la excepción: 34 de 35 expedientes los traen. */
    legalRepresentatives: z.array(section(LegalRepresentativeSchema)),
    revalidation: V2AdminClientRevalidationSchema
  })

export const V2AdminClientIndividualSchema = individualSchema(publishedSection)
export const V2AdminClientJuridicalSchema = juridicalSchema(publishedSection)

/**
 * Expediente completo.
 *
 * `individual` y `juridical` son excluyentes y los dirige `typePerson`: exactamente
 * uno viene poblado y el otro es `null`. No se colapsan en un solo campo
 * polimórfico porque el consumidor —el FEIC interno— renderiza dos formularios
 * distintos y necesita discriminar sin inspeccionar el contenido.
 */
const clientSchema = (section: SectionMode) =>
  z.object({
    id: PublicIdSchemas.client,
    /** Código correlativo legible; es por lo que la gente busca al cliente. */
    code: z.number().int(),
    typePerson: z.enum(['individual', 'juridical']),
    advisor: V2AdminClientUserRefSchema,
    company: z.object({ id: PublicIdSchemas.company, name: z.string() }).nullable(),
    agency: z.object({ id: PublicIdSchemas.agency, name: z.string() }).nullable(),
    contract: V2AdminClientContractSchema,
    place: V2AdminClientPlaceSchema,
    signed: z.boolean(),
    riskLevel: z.string().nullable(),
    /**
     * Hay un flujo del portal abierto: el formulario interno pasa a sólo-lectura
     * porque la edición vive del lado del cliente hasta que ese flujo termine. Es
     * la «pelota de un solo lado», y viaja en el detalle para que la UI no tenga
     * que pedirlo aparte para decidir si habilita el botón de guardar.
     */
    hasActiveFlow: z.boolean(),
    activeFlowStatus: z.string().nullable(),
    createdBy: V2AdminClientUserRefSchema,
    updatedBy: V2AdminClientUserRefSchema,
    createdAt: InstantSchema,
    updatedAt: InstantSchema,
    individual: individualSchema(section).nullable(),
    juridical: juridicalSchema(section).nullable()
  })

/** El expediente TAL COMO SE PUBLICA. De acá salen el OpenAPI, el SDK y los tipos. */
export const V2AdminClientSchema = clientSchema(publishedSection)

/**
 * El expediente TAL COMO EL REGISTRO LO GUARDÓ. Es el que hay que usar para
 * VALIDAR una respuesta — la web lo aplica en `assert-shape.ts`.
 *
 * ── POR QUÉ SON DOS Y NO UNO ─────────────────────────────────────────────────
 *
 * Porque el contrato publicado SOBRE-PROMETE, y arreglarlo es un trabajo aparte.
 *
 * El spec declara `currency` obligatorio en una cuenta bancaria, `quantity`
 * obligatorio en una inversión y `businessRelationship` no vacío. La API no
 * cumple ninguna de las tres: las secciones son `Mixed` en Mongo y devuelve lo
 * que haya guardado, incluidos los expedientes viejos y a medio llenar. Tres
 * clientes de producción no abrían por exactamente eso.
 *
 * ── LA DEUDA, ESCRITA PARA QUE NO SE PIERDA ──────────────────────────────────
 *
 * Corregir el spec para que diga la verdad son **4516 cambios que `oasdiff`
 * clasifica como incompatibles**: 1832 «became nullable», 1732 «became
 * optional», 768 `minLength`, y el resto entre `minItems` y tipos. No lo son en
 * el sentido que importa —la API ya se comportaba así; el contrato es el que
 * mentía— pero `oasdiff` no puede distinguir «se aflojó» de «se corrigió», y ese
 * guard existe para proteger a los consumidores del SDK, la app móvil entre
 * ellos. Meterlo en un hotfix habría colado una decisión de contrato dentro de
 * un arreglo urgente.
 *
 * MIENTRAS ESTO SEAN DOS, el spec le miente a quien lo lea: promete campos que
 * pueden no venir. Un consumidor nuevo puede confiar en esa promesa y romperse
 * en runtime, y el guard de CI no lo va a avisar, porque el contrato publicado
 * no cambió. El riesgo no desapareció: cambió de lugar y quedó anotado.
 *
 * CERRARLO = publicar este schema COMO el contrato, absorber los 4516 y avisar a
 * los consumidores. Es su propio PR, con versionado y nota de migración. Este
 * `export` desaparece el día que los dos vuelvan a ser uno.
 */
export const V2AdminClientStoredSchema = clientSchema(storedSection)

export type V2AdminClientUserRef = z.infer<typeof V2AdminClientUserRefSchema>
export type V2AdminClientRevalidation = z.infer<typeof V2AdminClientRevalidationSchema>
export type V2AdminClientIndividual = z.infer<typeof V2AdminClientIndividualSchema>
export type V2AdminClientJuridical = z.infer<typeof V2AdminClientJuridicalSchema>
export type V2AdminClient = z.infer<typeof V2AdminClientSchema>

/**
 * Estado derivado del ciclo de revalidación. No es un campo guardado: sale de la
 * firma de promesa en SAP, la fecha de contrato y la última verificación KYC, y
 * espeja el guard del motor de flujos.
 */
export const V2AdminClientRevalidationStatusSchema = z.enum([
  'up_to_date',
  'expired',
  'in_progress',
  'closed',
  'unverified'
])

/**
 * Flujo del portal ABIERTO del cliente. `null` cuando no hay ninguno — que es
 * distinto de «tuvo uno y terminó»: los terminales no cuentan.
 *
 * NO PUBLICA EL `accessToken`. El enlace del portal es una credencial: quien lo
 * tiene entra al expediente del cliente sin más. El listado legacy lo mandaba en
 * cada fila, así que una tabla de 436 clientes bajaba al navegador 436 enlaces
 * vivos para que la UI pudiera copiar UNO. El dueño natural del token es el
 * recurso de flujos del cliente, que ya lo devuelve y que se pide sólo cuando
 * alguien abre ese expediente.
 */
export const V2AdminClientActiveFlowSchema = z
  .object({
    /** Nombre de la definición del flujo; nulo en flujos legacy sin definición. */
    name: z.string().nullable(),
    /** Alta, validación o revalidación: por lo que filtra la UI. */
    context: z.string().nullable(),
    status: z.string(),
    validationType: z.string().nullable()
  })
  .nullable()

/**
 * Fila del listado administrativo de clientes.
 *
 * Es la TABLA, no el expediente: los campos que la lista muestra, filtra u
 * ordena. Quien necesita el expediente completo pide el detalle.
 *
 * CUATRO DIFERENCIAS CON LA FILA DE v1, todas por la misma razón —un contrato se
 * lee sin conocer la base de datos que lo produjo—:
 *
 * 1. **`emails`/`phones` son arreglos siempre.** En v1 el correo del individual
 *    era un string y el de la persona jurídica un arreglo, así que el tipo era
 *    `string | string[] | null` y cada consumidor tenía que desambiguarlo.
 * 2. **`hasNegotiations` y `hasActiveFlow` no viajan.** Son `negotiationCount > 0`
 *    y `activeFlow != null`: publicar un booleano derivado de un campo que está
 *    al lado invita a que discrepen. Como FILTRO siguen existiendo, porque ahí sí
 *    hacen falta.
 * 3. **Lo del flujo se agrupa en `activeFlow`.** En v1 eran cinco campos sueltos
 *    (`flowName`, `flowStatus`, `flowContext`, `validationType`, `hasActiveFlow`)
 *    que sólo tienen sentido juntos, y todos nulos a la vez cuando no hay flujo.
 * 4. **`revalidationDueDate` es una fecha de calendario**, no un `*At`. Vence un
 *    día, no a una hora: se deriva de la firma de promesa, que ya era un día.
 */
export const V2AdminClientListItemSchema = z.object({
  id: PublicIdSchemas.client,
  code: z.number().int(),
  typePerson: z.enum(['individual', 'juridical']),
  /** Nombre completo del individuo o razón social; compuesto si el legacy no lo trae armado. */
  name: z.string(),
  nit: z.string().nullable(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  /** Número del documento de identidad del individual; las jurídicas no tienen. */
  identification: z.string().nullable(),
  /** URL de la foto del expediente (individual). */
  photo: z.string().nullable(),
  advisor: V2AdminClientUserRefSchema,
  /** Negociaciones donde el cliente participa como owner. */
  negotiationCount: z.number().int().nonnegative(),
  activeFlow: V2AdminClientActiveFlowSchema,
  revalidation: z.object({
    status: V2AdminClientRevalidationStatusSchema,
    lastKycVerifiedAt: InstantSchema.nullable(),
    /** Un año (o la frecuencia del flujo) desde la firma de promesa. */
    dueDate: LocalDateSchema.nullable(),
    promiseDate: LocalDateSchema.nullable(),
    contractClosedAt: InstantSchema.nullable(),
    /** La negociación que dirige la fecha: explica POR QUÉ vence cuando vence. */
    sourceNegotiationCode: z.number().int().nullable(),
    sourceOrderSap: z.number().int().nullable()
  }),
  createdAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminClientsPageSchema = z.object({
  data: z.array(V2AdminClientListItemSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Conteo de una dimensión faceteada.
 *
 * `total` cuenta las filas de la dimensión IGNORANDO su propio filtro, y por eso
 * no es la suma de `values`: incluye a las que no tienen valor en ella (un
 * cliente sin flujo activo no aparece en ningún `flowStatus` pero existe). Es lo
 * que necesita el chip «Todos» para no mentir.
 *
 * Cada faceta ignora su propio filtro y respeta los demás: si contara sobre el
 * resultado ya filtrado, elegir «Vencido» dejaría en cero a los otros chips y el
 * usuario quedaría encerrado, sin poder ver cuántos hay del otro lado para
 * saltar allá.
 */
export const V2AdminClientsFacetSchema = z.object({
  total: z.number().int().nonnegative(),
  values: z.record(z.string(), z.number().int().nonnegative())
})

/**
 * Totales y facetas del listado, con los MISMOS filtros y sin paginación
 * (ADR 0019: las facetas no viven dentro de la colección, para no cobrárselas a
 * móvil, PAT e integraciones, que sólo la recorren).
 */
export const V2AdminClientsSummarySchema = z.object({
  /** Respeta la consulta completa, filtros faceteados incluidos. */
  total: z.number().int().nonnegative(),
  /**
   * Las cinco claves viajan siempre, en cero si no hay filas: un chip que
   * aparece y desaparece según el filtro salta de posición al seleccionarlo.
   */
  revalidationStatus: z.object({
    total: z.number().int().nonnegative(),
    values: z.object({
      up_to_date: z.number().int().nonnegative(),
      expired: z.number().int().nonnegative(),
      in_progress: z.number().int().nonnegative(),
      closed: z.number().int().nonnegative(),
      unverified: z.number().int().nonnegative()
    })
  }),
  /** Abierto: los estados del flujo son de la máquina del portal, no de este contrato. */
  flowStatus: V2AdminClientsFacetSchema,
  /** Abierto: los tipos los declaran las definiciones de flujo, que son data. */
  validationType: V2AdminClientsFacetSchema
})

export type V2AdminClientRevalidationStatus = z.infer<typeof V2AdminClientRevalidationStatusSchema>
export type V2AdminClientActiveFlow = z.infer<typeof V2AdminClientActiveFlowSchema>
export type V2AdminClientListItem = z.infer<typeof V2AdminClientListItemSchema>
export type V2AdminClientsPage = z.infer<typeof V2AdminClientsPageSchema>
export type V2AdminClientsFacet = z.infer<typeof V2AdminClientsFacetSchema>
export type V2AdminClientsSummary = z.infer<typeof V2AdminClientsSummarySchema>

/**
 * Entrada del SELECTOR de clientes.
 *
 * Reemplaza a tres endpoints del legacy que hacían lo mismo con distinta cara:
 * `GET /clients/selections` (el selector de negociaciones), `POST /clients/selects`
 * (traer varios por código) y la búsqueda para invitaciones que vivía colgada del
 * controller de usuarios. Los tres resolvían «dame clientes que casen con esto»;
 * lo único que cambiaba era por qué campo se filtraba.
 *
 * Ninguno proyectaba: devolvían el cliente entero con su expediente FEIC pegado
 * para llenar un desplegable de cinco campos. Acá el selector devuelve lo que un
 * selector necesita — identificar la fila y mostrarla— y nada más.
 *
 * El de códigos además arrastraba un N+1 en el controller: un `for` secuencial
 * que pedía cliente por cliente. Con un filtro `$in` eso deja de ser un bucle y
 * pasa a ser una consulta.
 */
export const V2AdminClientLookupItemSchema = z.object({
  id: PublicIdSchemas.client,
  code: z.number().int(),
  typePerson: z.enum(['individual', 'juridical']),
  /** Nombre completo del individuo o razón social de la empresa. */
  name: z.string(),
  nit: z.string().nullable(),
  /** Para que el selector muestre de quién es el cliente sin una segunda consulta. */
  advisor: V2AdminClientUserRefSchema
})

export const V2AdminClientLookupSchema = z.object({
  data: z.array(V2AdminClientLookupItemSchema)
})

export type V2AdminClientLookupItem = z.infer<typeof V2AdminClientLookupItemSchema>
export type V2AdminClientLookup = z.infer<typeof V2AdminClientLookupSchema>

/**
 * Disponibilidad de un documento de identidad.
 *
 * El formulario del FEIC lo consulta para avisar «este DPI ya está registrado»
 * antes de que el asesor termine de cargar el expediente. Devuelve un booleano y
 * NADA más: decir de quién es el documento convertiría el aviso en una forma de
 * averiguar datos de una persona a partir de su DPI.
 *
 * El endpoint legacy no exigía permiso alguno —bastaba un JWT—, así que
 * cualquier cuenta podía ir probando números de documento contra el padrón. En
 * v2 lo gobiernan los permisos de alta y edición de clientes, que son los que
 * tiene quien legítimamente está llenando una ficha.
 */
export const V2ClientIdentificationQuerySchema = z.object({
  /** `D` DPI, `P` pasaporte, `CN` certificado de nacimiento, `DF`/`DFD` extranjero. */
  type: requiredString(),
  number: requiredString()
})

export const V2ClientIdentificationAvailabilitySchema = z.object({
  taken: z.boolean()
})

/**
 * Reasignación masiva del asesor de una cartera.
 *
 * `advisor: null` DESASIGNA. El legacy lo modelaba como dos acciones distintas
 * (`update`/`delete`) sobre el mismo endpoint; son la misma operación con un
 * valor distinto, y un `null` explícito lo dice sin inventar un verbo.
 *
 * La respuesta informa CUÁNTOS expedientes cambiaron. El legacy devolvía `true`
 * y nada más: sus ramas de error no podían dispararse nunca —`updateMany`
 * siempre devuelve un objeto, así que el `if (!resultado)` era código muerto— y
 * una reasignación que no tocaba ningún cliente se reportaba como exitosa.
 */
export const V2ReassignClientAdvisorSchema = z.object({
  clients: z.array(PublicIdSchemas.client).min(1, 'Elige al menos un cliente'),
  advisor: PublicIdSchemas.user.nullable()
})

export const V2ReassignClientAdvisorResultSchema = z.object({
  /** Cuántos de los `cli_` pedidos existen y están vivos. */
  matched: z.number().int().nonnegative(),
  /** Cuántos cambiaron de verdad (los que ya tenían ese asesor no cuentan). */
  modified: z.number().int().nonnegative()
})

export type V2ClientIdentificationQuery = z.infer<typeof V2ClientIdentificationQuerySchema>
export type V2ClientIdentificationAvailability = z.infer<
  typeof V2ClientIdentificationAvailabilitySchema
>
export type V2ReassignClientAdvisorInput = z.infer<typeof V2ReassignClientAdvisorSchema>
export type V2ReassignClientAdvisorResult = z.infer<typeof V2ReassignClientAdvisorResultSchema>

/**
 * Contrato de ALTA del expediente.
 *
 * ESPEJA LA LECTURA: la ficha del cliente arriba y UNA de las dos personas
 * colgando, dirigida por `typePerson`. Lo que se lee es lo que se escribe, y el
 * consumidor no tiene que traducir entre dos formas del mismo expediente. El
 * legacy mandaba `{ client, applicant, pep, economicInformation, … }` con los
 * campos de la persona aplanados al lado de la ficha.
 *
 * EXIGE EL MÍNIMO, ACEPTA EL TODO. Los shapes salen del SSOT que ya valida estos
 * datos cuando el portal los escribe, pedidos en su nivel `draft`: individual =
 * nombre, apellido y documento; jurídica = razón social. El resto viaja opcional
 * y validado si viene. No es laxitud: un alta rápida crea el expediente con la
 * identidad y nada más —es lo que hace hoy el alta desde el portal—, y exigir el
 * expediente completo acá rompería ese flujo o forzaría a inventarle datos.
 *
 * TRES CAMPOS DEL LEGACY NO SE ACEPTAN. `folders`, `verification` y el
 * `riskLevel` de nivel superior estaban en el DTO de alta y **nadie los leía**:
 * el caso de uso nunca los tocó (el `riskLevel` que sí se guarda viaja en la
 * ficha). El portal se los mandaba vacíos en cada llamada. Un contrato que pide
 * campos que descarta le enseña al consumidor a mandar ruido.
 */
const V2CreateClientCardShape = {
  typePerson: z.enum(['individual', 'juridical']),
  /**
   * Se omite para que lo resuelva el servidor. Un asesor que da de alta un
   * cliente queda como SU asesor: mandar otro `usr_` en ese caso no es un
   * permiso que este endpoint conceda.
   */
  advisor: PublicIdSchemas.user.nullish(),
  /**
   * OBLIGATORIAS, y no por rigor: el esquema de Mongo las declara `required`, así
   * que un alta sin ellas ya fallaba — pero fallaba abajo, con un
   * `MONGO_VALIDATION_ERROR` que nombra un path de la base en vez de un campo del
   * contrato. Declararlas acá convierte ese error filtrado en un 400 de
   * validación que dice qué falta.
   */
  company: PublicIdSchemas.company,
  agency: PublicIdSchemas.agency,
  contract: z
    .object({
      startDate: LocalDateSchema.nullish(),
      updateDate: LocalDateSchema.nullish(),
      endDate: LocalDateSchema.nullish()
    })
    .optional(),
  place: z
    .object({
      country: requiredString(),
      department: z.number().int().nullish(),
      municipality: z.number().int().nullish()
    })
    .optional(),
  riskLevel: z.string().nullish()
}

/** Rama individual del alta. La foto llega en base64, como hoy la manda RENAP. */
export const V2CreateClientIndividualSchema = z.object({
  applicant: openShape(IndividualApplicant.forLevel('draft')),
  pep: writeSection(PepDeclarationShape).nullish(),
  economicInformation: writeSection(EconomicDeclarationShape).nullish(),
  equityStatement: writeSection(EquityDeclarationShape).nullish(),
  legalRepresentatives: z.array(writeSection(LegalRepresentativeSchema)).optional(),
  photo: z.string().nullish(),
  renapVerification: z
    .object({
      cui: requiredString(),
      verifiedAt: InstantSchema,
      fields: z.array(z.string())
    })
    .nullish()
})

/** Rama jurídica. Acá el solicitante ES la empresa y la persona va de representante. */
export const V2CreateClientJuridicalSchema = z.object({
  applicant: openShape(CompanyApplicant.forLevel('draft')),
  economicInformation: writeSection(EconomicDeclarationJuridicalShape).nullish(),
  equityStatement: writeSection(EquityDeclarationJuridicalShape).nullish(),
  legalRepresentatives: z.array(writeSection(LegalRepresentativeSchema)).optional()
})

export const V2CreateClientInputSchema = z
  .object({
    ...V2CreateClientCardShape,
    individual: V2CreateClientIndividualSchema.optional(),
    juridical: V2CreateClientJuridicalSchema.optional()
  })
  .superRefine((value, ctx) => {
    // La rama tiene que coincidir con `typePerson`. Mandar la otra no es un
    // descuido inocuo: sería un expediente jurídico con datos de persona
    // guardados en una colección que nadie va a leer.
    const expected = value.typePerson
    const other = expected === 'individual' ? 'juridical' : 'individual'
    if (!value[expected]) {
      ctx.addIssue({
        path: [expected],
        code: z.ZodIssueCode.custom,
        message: `Falta el expediente ${expected === 'individual' ? 'de la persona' : 'de la empresa'}`
      })
    }
    if (value[other]) {
      ctx.addIssue({
        path: [other],
        code: z.ZodIssueCode.custom,
        message: `Un expediente ${expected} no lleva la rama ${other}`
      })
    }
  })

export type V2CreateClientInput = z.infer<typeof V2CreateClientInputSchema>

/**
 * Contrato de EDICIÓN del expediente.
 *
 * MERGE POR SECCIÓN, y es la diferencia que justifica la tajada. El legacy
 * recibía un `PATCH` y hacía un reemplazo: pasaba `applicant`, `pep`,
 * `economicInformation` y `equityStatement` enteros a `findOneAndUpdate`, así que
 * cualquier sección que el formulario no mandara se pisaba. Acá una sección que
 * NO viene no se toca; una que viene se reemplaza completa. Es lo que el
 * formulario ya hace al guardar, y cierra la familia de bugs donde una pantalla
 * que edita un dato borra otro que ni siquiera muestra.
 *
 * LAS FILAS NO TIENEN IDENTIDAD EN EL WIRE, a propósito. Cada fila repetible del
 * FEIC —ingresos, cuentas, vehículos— guarda hoy un `id` uuid que genera el
 * navegador: es una key de React, no identifica ninguna entidad, y ningún otro
 * consumidor la referencia. Como la sección se reemplaza como unidad, el servidor
 * nunca necesita correlacionar filas: publicarla obligaría a migrar miles de
 * uuids a ids públicos para sostener una identidad que nadie usa. La lista es el
 * valor; su orden, el orden.
 *
 * `typePerson` NO se acepta: convertir una persona individual en jurídica no es
 * editar un campo, es cambiar de expediente —otra colección, otro contrato— y
 * fingir que es un `PATCH` invita a hacerlo por accidente.
 */
export const V2UpdateClientInputSchema = z
  .object({
    advisor: PublicIdSchemas.user.nullish(),
    company: PublicIdSchemas.company.optional(),
    agency: PublicIdSchemas.agency.optional(),
    contract: z
      .object({
        startDate: LocalDateSchema.nullish(),
        updateDate: LocalDateSchema.nullish(),
        endDate: LocalDateSchema.nullish()
      })
      .optional(),
    place: z
      .object({
        country: requiredString(),
        department: z.number().int().nullish(),
        municipality: z.number().int().nullish()
      })
      .optional(),
    riskLevel: z.string().nullish(),
    signed: z.boolean().optional(),
    individual: V2CreateClientIndividualSchema.partial().optional(),
    juridical: V2CreateClientJuridicalSchema.partial().optional()
  })
  .superRefine((value, ctx) => {
    if (value.individual && value.juridical) {
      ctx.addIssue({
        path: ['juridical'],
        code: z.ZodIssueCode.custom,
        message: 'Un expediente es individual o jurídico, no las dos cosas'
      })
    }
  })

export type V2UpdateClientInput = z.infer<typeof V2UpdateClientInputSchema>
